import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { NavItemList, NavDropdownConfig } from './types';
import { getIconComponent } from './NavigationIcons';

export interface MobileNavigationProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  mainItems: NavItemList;
  dropdowns: NavDropdownConfig[];
  adminDropdown: NavDropdownConfig | null;
  location: { pathname: string };
  followUpCount: number;
  unreadMessagesCount: number;
  /** Pending manufacturer approvals, shown on the Fleet > Approvals item. */
  fleetApprovalsCount?: number;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  mainItems,
  dropdowns,
  adminDropdown,
  location,
  followUpCount,
  unreadMessagesCount,
  fleetApprovalsCount = 0,
}) => (
  <Drawer
    anchor="left"
    open={mobileMenuOpen}
    onClose={() => setMobileMenuOpen(false)}
    ModalProps={{
      keepMounted: true, // Better mobile performance
    }}
    sx={{
      '& .MuiDrawer-paper': {
        width: 280,
        boxSizing: 'border-box',
      },
    }}
  >
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="h6" color="primary">
        Navigation
      </Typography>
    </Box>
    <List>
      {/* Main Items - Dashboard, Inbox, Compliance, QR Codes */}
      {mainItems.map((item) => (
        <ListItemButton
          key={item.path}
          component={Link}
          to={item.path}
          onClick={() => {
            setMobileMenuOpen(false);
          }}
          selected={location.pathname === item.path}
        >
          <ListItemIcon>
            {item.label === 'Inbox' && unreadMessagesCount > 0 ? (
              <Badge badgeContent={unreadMessagesCount} color="error">
                {getIconComponent(item.label)}
              </Badge>
            ) : (
              getIconComponent(item.label)
            )}
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}

      {/* Dynamic dropdown sections (Jobs, Leads, Financials, Direct, Settings) */}
      {dropdowns.map((dropdown) => (
        <Accordion key={dropdown.key}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={(theme) => ({
              backgroundColor: dropdown.color === 'secondary'
                ? alpha(theme.palette.secondary.main, 0.08)
                : undefined,
            })}
          >
            <ListItemIcon>
              {dropdown.icon}
            </ListItemIcon>
            <Typography fontWeight="medium">{dropdown.label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {dropdown.items.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
                selected={location.pathname === item.path}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>
                  {(item.label === 'Follow-ups' && followUpCount > 0) ||
                  (item.label === 'Approvals' && fleetApprovalsCount > 0)
                    ? <Badge badgeContent={item.label === 'Follow-ups' ? followUpCount : fleetApprovalsCount} color="warning" max={99}>{getIconComponent(item.label)}</Badge>
                    : getIconComponent(item.label)}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Platform Admin Section - Only for platform admins */}
      {adminDropdown && adminDropdown.items.length > 0 && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={(theme) => ({
              backgroundColor: alpha(theme.palette.warning.main, 0.08)
            })}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <Typography fontWeight="medium">Admin</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {adminDropdown.items.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
                selected={location.pathname === item.path}
                sx={(theme) => ({
                  pl: 4,
                  backgroundColor: alpha(theme.palette.warning.main, 0.02)
                })}
              >
                <ListItemIcon>
                  {getIconComponent(item.label)}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </List>
  </Drawer>
);
